"use client";

import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { KeyRound, PlayCircle, Plus, Shield } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { IconTile } from "@/components/ui/icon-tile";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { lawyerSelectors } from "../redux/lawyer/selectors";
import {
  buildDeterministicSamlSpEntityId,
  getSsoAuthDomain
} from "../helpers/sso";
import type {
  SsoProviderConfigDTO,
  SsoProviderType,
  SsoSetupStateDTO,
  ValidateSsoProviderResponseDTO
} from "../api/types/lawFirm";
import { LawFirmsAPI } from "../api/lawFirms";

const showToast = (
  variant: "success" | "error",
  title: string,
  description?: string
) => {
  const options = { description, duration: 6000 };
  if (variant === "success") toast.success(title, options);
  else toast.error(title, options);
};

type SsoFieldProps = React.ComponentProps<"input"> & {
  label: string;
  helpText?: string;
};

function SsoField({ label, helpText, ...inputProps }: SsoFieldProps) {
  const id = useId();
  return (
    <div className="flex w-full flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...inputProps} />
      {helpText ? <p className="text-sm text-fg-3">{helpText}</p> : null}
    </div>
  );
}

type ProviderListItem = {
  provider_id: string;
  type: SsoProviderType | string;
  label?: string | null;
  enabled?: boolean | null;
};

type ProviderConfigRecord = SsoProviderConfigDTO;

const getValidationCheckTitle = (name: string): string => {
  switch (String(name || "").trim()) {
    case "firm_provider_enabled":
      return "Provider is enabled for this firm";
    case "identity_platform_config_found":
      return "Provider configuration found";
    case "identity_platform_enabled":
      return "Provider is enabled";
    case "display_name_present":
      return "Sign-in button label is set";
    case "issuer_present":
      return "Issuer URL is set";
    case "oidc_discovery_fetch":
      return "Discovery endpoint is reachable";
    case "oidc_discovery_fields":
      return "Required sign-in endpoints are available";
    case "oidc_jwks_fetch":
      return "Signing keys are available";
    case "idp_entity_id_present":
      return "Identity provider issuer is set";
    case "sso_url_present":
      return "Single sign-on URL is set";
    case "certificate_present":
      return "Signing certificate is present";
    case "callback_uri_matches":
      return "Callback URL matches";
    case "sp_entity_id_matches":
      return "SP Entity ID / Audience URI matches";
    default:
      return "Configuration check";
  }
};

export function LawFirmSsoManagement() {
  const navigate = useNavigate();
  const lawyer = useSelector(lawyerSelectors.selectAll);
  const lawFirmId =
    (lawyer as { law_firm_id?: string | null })?.law_firm_id || null;
  const role = String((lawyer as { role?: string | null })?.role || "")
    .trim()
    .toLowerCase();
  const canManageSso =
    role === "owner" || role === "admin" || role === "superadmin";

  const userEmail = String((lawyer as { email?: string | null })?.email || "")
    .trim()
    .toLowerCase();

  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState<SsoSetupStateDTO | null>(null);
  const [configs, setConfigs] = useState<ProviderConfigRecord[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null
  );

  const [validation, setValidation] =
    useState<ValidateSsoProviderResponseDTO | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const authDomain = useMemo(() => getSsoAuthDomain(), []);

  const expectedCallbackUri = useMemo(() => {
    return `https://${authDomain}/__/auth/handler`;
  }, [authDomain]);

  const selectedProvider = useMemo(() => {
    if (!selectedProviderId) return null;
    const p = (state?.providers ?? []).find(
      (x) => String(x.provider_id || "").trim() === selectedProviderId
    );
    return (p ?? null) as ProviderListItem | null;
  }, [state?.providers, selectedProviderId]);

  const selectedConfig = useMemo(() => {
    if (!selectedProviderId) return null;
    return (
      configs.find(
        (c) => String(c.provider_id || "").trim() === selectedProviderId
      ) ?? null
    );
  }, [configs, selectedProviderId]);

  const expectedSamlSpEntityId = useMemo(() => {
    if (!lawFirmId || !selectedProviderId) {
      return "";
    }

    return buildDeterministicSamlSpEntityId({
      authDomain,
      lawFirmId,
      providerId: selectedProviderId
    });
  }, [authDomain, lawFirmId, selectedProviderId]);

  const refresh = useCallback(async () => {
    if (!lawFirmId) return;
    setIsLoading(true);
    try {
      const next = await LawFirmsAPI.getSsoSetup(lawFirmId);
      setState(next);
      if (!selectedProviderId && next.providers?.length) {
        setSelectedProviderId(
          String(next.providers[0]?.provider_id || "").trim() || null
        );
      }
      const cfgResp = await LawFirmsAPI.getSsoProviderConfigs(lawFirmId);
      const list = Array.isArray(cfgResp.configs) ? cfgResp.configs : [];
      setConfigs(list);
    } finally {
      setIsLoading(false);
    }
  }, [lawFirmId, selectedProviderId]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const [draftEnabled, setDraftEnabled] = useState(true);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftOidcIssuer, setDraftOidcIssuer] = useState("");
  const [draftOidcClientId, setDraftOidcClientId] = useState("");
  const [draftOidcClientSecret, setDraftOidcClientSecret] = useState("");

  const [draftSamlEntityId, setDraftSamlEntityId] = useState("");
  const [draftSamlSsoUrl, setDraftSamlSsoUrl] = useState("");
  const [draftSamlCert, setDraftSamlCert] = useState("");

  useEffect(() => {
    const p = selectedProvider;
    const c = selectedConfig;
    if (!p || !c) return;

    setDraftEnabled(Boolean(c.enabled ?? p.enabled ?? true));
    setDraftLabel(String(c.displayName ?? p.label ?? p.provider_id ?? ""));

    const t = String(p.type || c.type || "")
      .trim()
      .toLowerCase();
    if (t === "oidc") {
      setDraftOidcIssuer(String(c.issuer ?? ""));
      setDraftOidcClientId(String(c.clientId ?? ""));
      setDraftOidcClientSecret("");
    } else {
      setDraftSamlEntityId(
        String(
          c.idpEntityId ??
            (typeof c.idpConfig === "object" && c.idpConfig
              ? (c.idpConfig as any).idpEntityId
              : "") ??
            ""
        )
      );
      setDraftSamlSsoUrl(
        String(
          c.ssoUrl ??
            (typeof c.idpConfig === "object" && c.idpConfig
              ? (c.idpConfig as any).ssoUrl
              : "") ??
            ""
        )
      );
      // Certificate is not safely round-trippable via UI unless we include it.
      // We keep it blank and rely on config.idpConfig if present when saving.
      setDraftSamlCert("");
    }
  }, [selectedProvider, selectedConfig]);

  const saveProvider = async (
    providerId: string,
    providerType: string,
    overrides?: {
      enabled?: boolean;
    }
  ) => {
    if (!lawFirmId) return;
    const type = providerType === "saml" ? "saml" : "oidc";

    const base = {
      provider_id: providerId,
      type,
      enabled: overrides?.enabled ?? draftEnabled,
      label: draftLabel || null
    } as const;

    const cfg = selectedConfig;
    if (!cfg) return;

    const config =
      type === "oidc"
        ? {
            issuer: draftOidcIssuer,
            clientId: draftOidcClientId,
            ...(draftOidcClientSecret.trim()
              ? { clientSecret: draftOidcClientSecret }
              : {})
          }
        : {
            idpConfig: {
              ...((cfg.idpConfig as Record<string, unknown> | undefined) ?? {}),
              idpEntityId: draftSamlEntityId,
              ssoUrl: draftSamlSsoUrl,
              idpCertificates: draftSamlCert.trim()
                ? [{ x509Certificate: draftSamlCert }]
                : ((cfg.idpConfig as Record<string, unknown> | undefined)
                    ?.idpCertificates as unknown[]) ?? []
            },
            spConfig:
              (cfg.spConfig as Record<string, unknown> | undefined) ?? {}
          };

    setIsLoading(true);
    try {
      await LawFirmsAPI.upsertSsoProvider(lawFirmId, {
        ...base,
        config
      });
      await refresh();
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProviderEnabled = async (
    providerId: string,
    providerType: string,
    nextEnabled: boolean
  ) => {
    const cfg =
      configs.find((c) => String(c.provider_id || "").trim() === providerId) ??
      null;
    if (!cfg) return;
    if (!lawFirmId) return;
    if (providerId !== selectedProviderId) {
      const type = providerType === "saml" ? "saml" : "oidc";
      const provider =
        (state?.providers ?? []).find(
          (p) => String(p.provider_id || "").trim() === providerId
        ) ?? null;
      const existingIdpConfig =
        (cfg.idpConfig as Record<string, unknown> | undefined) ?? {};
      const config =
        type === "oidc"
          ? {
              issuer: String(cfg.issuer ?? ""),
              clientId: String(cfg.clientId ?? "")
            }
          : {
              idpConfig: {
                ...existingIdpConfig,
                idpEntityId:
                  (cfg.idpEntityId ??
                    (typeof cfg.idpConfig === "object" && cfg.idpConfig
                      ? (cfg.idpConfig as any).idpEntityId
                      : "")) ||
                  "",
                ssoUrl:
                  (cfg.ssoUrl ??
                    (typeof cfg.idpConfig === "object" && cfg.idpConfig
                      ? (cfg.idpConfig as any).ssoUrl
                      : "")) ||
                  ""
              },
              spConfig:
                (cfg.spConfig as Record<string, unknown> | undefined) ?? {}
            };
      setIsLoading(true);
      try {
        await LawFirmsAPI.upsertSsoProvider(lawFirmId, {
          provider_id: providerId,
          type,
          enabled: nextEnabled,
          label: cfg.displayName ?? provider?.label ?? null,
          config
        });
        await refresh();
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setDraftEnabled(nextEnabled);
    await saveProvider(providerId, providerType, { enabled: nextEnabled });
  };

  const handleValidateConfiguration = async () => {
    if (!lawFirmId || !selectedProvider) return;
    if (isValidating) return;
    setIsValidating(true);
    try {
      const type = String(selectedProvider.type || "")
        .trim()
        .toLowerCase();
      const resp = await LawFirmsAPI.validateSsoProvider(lawFirmId, {
        provider_id: String(selectedProvider.provider_id || "").trim(),
        provider_type: type === "saml" ? "saml" : "oidc",
        expected_callback_uri: expectedCallbackUri
      });
      setValidation(resp);

      const failed = (resp.checks || []).find((c) => !c.ok);
      if (resp.ok) {
        showToast(
          "success",
          "Validation passed",
          "Your SSO configuration checks completed successfully."
        );
      } else {
        showToast(
          "error",
          "Validation failed",
          failed
            ? `${getValidationCheckTitle(failed.name)}${
                failed.details ? `: ${failed.details}` : ""
              }`
            : "One or more checks failed."
        );
      }
    } catch (e: any) {
      showToast(
        "error",
        "Validation failed",
        String(e?.message ?? e ?? "Unknown error")
      );
    } finally {
      setIsValidating(false);
    }
  };

  if (!lawFirmId) {
    return (
      <div className="flex w-full flex-col items-start gap-4 bg-surface p-6">
        <PageHeader
          title="SSO Configuration"
          description="Could not determine your law firm. Please contact support."
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/cases")}
        >
          Back
        </Button>
      </div>
    );
  }

  if (!canManageSso) {
    return (
      <div className="flex w-full flex-col items-start gap-4 bg-surface p-6">
        <PageHeader
          title="SSO Configuration"
          description="You don’t have permission to configure SSO for this firm."
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/cases")}
        >
          Back
        </Button>
      </div>
    );
  }

  const providers = (state?.providers ?? []) as ProviderListItem[];

  return (
    <div className="flex h-full w-full items-start overflow-hidden bg-surface">
      <div className="flex w-64 flex-none flex-col items-start self-stretch border-r border-line-2 bg-chrome mobile:hidden">
        <div className="flex h-9 w-full flex-none items-center justify-between px-3">
          <span className="text-base font-medium text-fg">
            Identity Providers
          </span>
          {providers.length === 0 ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Set Up SSO"
              onClick={() => navigate("/law-firm/sso-setup")}
            >
              <Plus />
            </Button>
          ) : null}
        </div>
        <div className="flex w-full shrink-0 grow basis-0 flex-col items-start overflow-y-auto px-2 pb-2">
          <div className="flex w-full flex-col items-start gap-0.5">
            {providers.map((p) => {
              const id = String(p.provider_id || "").trim();
              const isSelected = id === selectedProviderId;
              const enabled = Boolean(p.enabled);
              return (
                <div
                  key={id}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors duration-100",
                    isSelected ? "bg-fill-3" : "hover:bg-fill-1"
                  )}
                  onClick={() => setSelectedProviderId(id)}
                >
                  <IconTile
                    size="lg"
                    icon={enabled ? Shield : KeyRound}
                    className={cn(isSelected && "text-fg")}
                  />
                  <div className="flex min-w-0 shrink-0 grow basis-0 flex-col items-start gap-1">
                    <span
                      className={cn(
                        "w-full truncate text-base text-fg",
                        enabled && "font-medium"
                      )}
                    >
                      {String(p.label || p.provider_id || "Provider")}
                    </span>
                    <StatusBadge tone={enabled ? "success" : "neutral"}>
                      {enabled ? "Active" : "Inactive"}
                    </StatusBadge>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Switch
                      size="sm"
                      checked={enabled}
                      onCheckedChange={(checked) => {
                        toggleProviderEnabled(
                          id,
                          String(p.type || ""),
                          checked
                        ).catch((error) => {
                          showToast(
                            "error",
                            "Couldn't save provider",
                            String(
                              (error as Error)?.message ??
                                error ??
                                "Unknown error"
                            )
                          );
                        });
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col items-start self-stretch overflow-hidden bg-surface">
        <div className="flex w-full shrink-0 grow basis-0 flex-col items-start overflow-y-auto">
          {!selectedProvider || !selectedConfig ? (
            <div className="flex w-full shrink-0 grow basis-0 items-center justify-center">
              <EmptyState
                icon={Shield}
                title="No SSO Provider Configured Yet"
                description="Set up your firm's SSO provider to view and manage its configuration here."
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate("/law-firm/sso-setup")}
                  >
                    Set Up SSO
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-190 flex-col gap-8 px-12 pt-10 pb-24 mobile:px-4 mobile:pt-6">
              <section className="flex flex-col gap-3">
                <SectionHeader
                  title="Basic Configuration"
                  description="Edit the core settings for your identity provider."
                  actions={
                    <Label className="flex items-center gap-2 text-base text-fg font-normal leading-normal">
                      <Switch
                        checked={draftEnabled}
                        onCheckedChange={setDraftEnabled}
                      />
                      Enabled
                    </Label>
                  }
                />
                <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
                  <SsoField
                    label="Display Name"
                    helpText="Shown on the login button."
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                  />

                  {String(selectedProvider.type || "").toLowerCase() ===
                  "oidc" ? (
                    <>
                      <SsoField
                        label="Issuer"
                        value={draftOidcIssuer}
                        onChange={(e) => setDraftOidcIssuer(e.target.value)}
                      />
                      <SsoField
                        label="Client ID"
                        value={draftOidcClientId}
                        onChange={(e) => setDraftOidcClientId(e.target.value)}
                      />
                      <SsoField
                        label="Client Secret (Optional)"
                        helpText="Leave blank to keep existing secret."
                        type="password"
                        placeholder="••••••••••••••••"
                        value={draftOidcClientSecret}
                        onChange={(e) =>
                          setDraftOidcClientSecret(e.target.value)
                        }
                      />
                    </>
                  ) : (
                    <>
                      <SsoField
                        label="Entity ID (Issuer)"
                        value={draftSamlEntityId}
                        onChange={(e) => setDraftSamlEntityId(e.target.value)}
                      />
                      <SsoField
                        label="SSO URL"
                        value={draftSamlSsoUrl}
                        onChange={(e) => setDraftSamlSsoUrl(e.target.value)}
                      />
                      <SsoField
                        label="SP Entity ID / Audience URI"
                        helpText="CaseBlink defines this SAML audience URI. Copy it into your IdP app exactly as shown."
                        readOnly={true}
                        value={expectedSamlSpEntityId}
                        placeholder="Not available"
                      />
                      <SsoField
                        label="Certificate (PEM)"
                        helpText="Optional: paste to update. Leave blank to keep existing."
                        value={draftSamlCert}
                        onChange={(e) => setDraftSamlCert(e.target.value)}
                      />
                    </>
                  )}
                </div>
              </section>

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isValidating}
                  onClick={handleValidateConfiguration}
                >
                  {isValidating ? <Spinner /> : <PlayCircle />}
                  Validate Configuration
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isLoading}
                  onClick={() => {
                    saveProvider(
                      String(selectedProvider.provider_id || ""),
                      String(selectedProvider.type || "")
                    ).catch((error) => {
                      showToast(
                        "error",
                        "Couldn't save provider",
                        String(
                          (error as Error)?.message ?? error ?? "Unknown error"
                        )
                      );
                    });
                  }}
                >
                  {isLoading ? <Spinner /> : null}
                  Save Changes
                </Button>
              </div>

              {validation ? (
                <section className="flex flex-col gap-3">
                  <SectionHeader
                    title="Validation Results"
                    actions={
                      <StatusBadge tone={validation.ok ? "success" : "danger"}>
                        {validation.ok ? "Passed" : "Failed"}
                      </StatusBadge>
                    }
                  />
                  <div className="flex w-full flex-col divide-y divide-line-1 rounded-lg bg-elevated ring-1 ring-line-2">
                    {(validation.checks || []).map((c) => (
                      <div
                        key={c.name}
                        className="flex w-full items-start justify-between gap-4 px-4 py-2.5"
                      >
                        <span className="text-base text-fg">
                          {getValidationCheckTitle(c.name)}
                        </span>
                        <span
                          className={cn(
                            "text-sm",
                            c.ok ? "text-fg-3" : "text-danger"
                          )}
                        >
                          {c.ok ? "OK" : c.details || "Failed"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LawFirmSsoManagement;
